import {appConfig} from '@config';
import {Lang, useCompetitionImamrezaContext} from '@context/CompetitionImamrezaContext';
import {useScopedTranslation} from '@hooks/useScopedTranslation';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	useTheme
} from '@mui/material';
import {getSafeLangImage} from '@utils';
import axios from 'axios';
import React, {useCallback, useEffect, useState} from 'react';
import ReactHtmlParser from 'react-html-parser';
import PhoneInput, {isValidPhoneNumber} from 'react-phone-number-input';
import {useNavigate} from 'react-router-dom';
import bg from './assets/images/m3/bg.webp';
import desc_ar from './assets/images/m3/desc-ar.webp';
import desc_en from './assets/images/m3/desc-en.webp';
import desc_fa from './assets/images/m3/desc-fa.webp';
import mf from './assets/images/m3/mf.webp';
import 'react-phone-number-input/style.css';
import title_ar from './assets/images/m3/title-ar.webp';
import title_en from './assets/images/m3/title-en.webp';
import title_fa from './assets/images/m3/title-fa.webp';

type IProps = {
	state?: string;
};

type LastData = {
	competition: number;
	phone: string;
	referrerCode: string | null;
	referrerFrom: string;
	results: Array<number>;
	gender: string;
	lang: string;
};

const titleImage: Record<Lang, string> = {
	en: title_en,
	fa: title_fa,
	ar: title_ar
};

const descImage: Record<Lang, string> = {
	en: desc_en,
	fa: desc_fa,
	ar: desc_ar
};

export default function Mobile(props: IProps) {
	const {state} = props;
	const theme = useTheme();
	const {t} = useScopedTranslation();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [phoneValid, setPhoneValid] = useState<boolean | null>(null);
	const [showExistDialog, setShowExistDialog] = useState(false);
	const [pendingReferralCode, setPendingReferralCode] = useState<string | null>(null);
	
	const {
		competition,
		mission,
		setMission,
		results,
		setResults,
		phone,
		setPhone,
		gender,
		setGender,
		type,
		setType,
		referrerCode,
		referralCode,
		setReferralCode,
		referrerFrom,
		lang
	} = useCompetitionImamrezaContext();
	
	// Redirect if already completed
	useEffect(() => {
		const isDone = localStorage.getItem('imamreza_competition_done');
		if (isDone === 'true') {
			setMission(0);
			// navigate('/');
		}
	}, []);
	
	useEffect(() => {
		setTimeout(() => {
			if (loading) {
				setLoading(false);
			}
		}, 10000);
	}, [loading]);
	
	const sendData = useCallback(
		async (lastData: LastData) => {
			try {
				if (isValidPhoneNumber(phone)) {
					setLoading(true);
					setPhoneValid(true);
					const res = await axios({
						url: `${appConfig.competition2.url}imamreza`,
						method: 'POST',
						data: lastData
					});
					
					if (res.data.success) {
						localStorage.setItem('imamreza_competition_done', 'true');
						setReferralCode(res.data.result?.referralCode);
						setGender(res.data.result?.gender);
						setType(res.data.result?.type);
						if (res.data?.result?.exist) {
							setPendingReferralCode(res.data.result?.referralCode);
							setShowExistDialog(true);
						} else {
							navigate(`/competition/result/${res.data.result?.referralCode}`);
						}
					} else {
						setLoading(false);
					}
				} else {
					setLoading(false);
					setPhoneValid(false);
				}
			} catch (err) {
				setLoading(false);
			}
		},
		[phone]
	);
	
	async function sendDataObject() {
		let lastData: LastData = {
			competition: 3,
			phone,
			results,
			referrerCode,
			referrerFrom,
			gender: gender!,
			lang
		};
		await sendData(lastData);
	}
	
	return (
		<Box
			className="w-full h-[calc(100vh-55px)] overflow-hidden justify-start overflow-y-auto pb-20"
			sx={{
				backgroundImage: `url(${bg})`,
				backgroundRepeat: 'no-repeat',
				backgroundSize: 'cover'
			}}
		>
			<div className="flex flex-col w-10/12 h-fit justify-center items-center gap-y-3 mx-auto mt-10">
				<img className="w-full" src={getSafeLangImage(titleImage, lang)} alt="title"/>
			</div>
			<div className="w-10/12 flex justify-center relative mt-auto mx-auto">
				<img src={mf} alt="boy" className="w-auto h-96 object-contain"/>
			</div>
			<div className="w-8/12 flex justify-center relative mt-auto mx-auto">
				<img src={getSafeLangImage(descImage, lang)} alt="desc" className="w-auto h-fit object-contain"/>
			</div>
			<div className="flex flex-col justify-start mt-10">
				<div>
					<Box className="flex flex-col w-11/12 cursor-pointer rtl m-auto gap-y-5">
						<PhoneInput
							international={true}
							placeholder={t('phone_number')}
							defaultCountry="IR"
							value={phone}
							onChange={(phoneNumber) => setPhone(phoneNumber as string)}
							limitMaxLength={true}
							className={`phone-input w-10/12 h-12 p-5 rounded-3xl ltr text-center m-auto ${
								phoneValid === false
									? 'phone-input-error border-red-800 bg-red-400'
									: 'border-[#0b152b] bg-green-900'
							}`}
						/>
						<div className="flex flex-row w-full justify-center gap-x-5">
							<div
								className={`w-24 text-center font-bold rounded-2xl px-3 py-1 cursor-pointer transition-all ${
									gender === 'male' ? 'bg-green-800 text-white' : 'bg-gray-200 text-[#D69528]'
								}`}
								onClick={() => setGender('male')}
							>
								{t('male')}
							</div>
							
							<div
								className={`w-24 text-center font-bold rounded-2xl px-3 py-1 cursor-pointer transition-all ${
									gender === 'female' ? 'bg-green-800 text-white' : 'bg-gray-200 text-[#D69528]'
								}`}
								onClick={() => setGender('female')}
							>
								{t('female')}
							</div>
						</div>
					</Box>
					
					{!loading && gender !== undefined && (
						<Box className="w-full flex mt-5 justify-center">
							<div
								className="min-w-3/12 w-fit h-8 px-5 bg-white rounded-3xl text-[#D69528] flex text-center items-center justify-center cursor-pointer font-bold">
								<div onClick={sendDataObject}>{t('send')}</div>
							</div>
						</Box>
					)}
					
					{loading && (
						<div className="w-full flex justify-center my-10">
							<CircularProgress sx={{color: '#0b152b'}}/>
						</div>
					)}
				</div>
			</div>
			
			{/* Exist Dialog */}
			<Dialog
				open={showExistDialog}
				onClose={() => {
					setShowExistDialog(false);
					navigate(`/competition/result/${pendingReferralCode}`);
				}}
			>
				<Box dir={lang === 'en' ? 'ltr' : 'rtl'}>
					<DialogTitle>{t('warning')}</DialogTitle>
					<DialogContent>
						<DialogContentText>
							{ReactHtmlParser(t('already_exist_message'))}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => {
								setShowExistDialog(false);
								if (pendingReferralCode) {
									navigate(`/competition/result/${pendingReferralCode}`);
								}
							}}
						>
							{t('ok')}
						</Button>
					</DialogActions>
				</Box>
			</Dialog>
		</Box>
	);
}
